import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ArrowUpRight, X, ZoomIn } from "lucide-react";

export const ProductCard = ({ product, index }) => {
  const { code, name, category, image, rating, ratingsCount, amazonUrl, isNew, takeaway } = product;
  const [zoomOpen, setZoomOpen] = useState(false);

  useEffect(() => {
    if (!zoomOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setZoomOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [zoomOpen]);

  return (
    <>
      <motion.article
      data-testid={`product-card-${code}`}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="group relative flex flex-col bg-card2 border hairline p-7 hover:border-brass/40 transition-colors duration-300"
    >
      <span
        aria-hidden="true"
        className="pointer-events-none select-none absolute -top-4 right-4 font-serif text-7xl text-charcoal/[0.05] group-hover:text-brass/10 transition-colors duration-500"
      >
        {code.split("-")[1]}
      </span>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-xs uppercase tracking-[0.2em] font-medium text-amber-800 bg-amber-500/10 px-2.5 py-1 rounded-sm border border-amber-800/20">
          {code}
        </span>
        <span className="font-mono text-[11px] uppercase tracking-widest text-charcoal/50">
          {category}
        </span>
        <span className="font-mono text-[11px] uppercase tracking-widest text-charcoal/50">
          · Amazon
        </span>
        {isNew && (
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-parchment bg-brass px-2 py-0.5 rounded-sm">
            New
          </span>
        )}
      </div>

      {image && (
        <button
          type="button"
          data-testid={`product-image-zoom-trigger-${code}`}
          onClick={() => setZoomOpen(true)}
          aria-label={`View a larger photo of ${name}`}
          className="mt-5 block w-full relative overflow-hidden border hairline bg-white cursor-zoom-in"
        >
          <img
            data-testid={`product-image-${code}`}
            src={image}
            alt={name}
            loading="lazy"
            className="aspect-[4/3] w-full object-contain p-3 group-hover:scale-105 transition-transform duration-500 ease-out"
          />
          <span className="absolute bottom-2 right-2 flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-charcoal/60 bg-parchment/90 border hairline px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <ZoomIn size={12} /> Zoom
          </span>
        </button>
      )}

      <h3 className="mt-5 font-serif text-xl sm:text-2xl font-medium leading-snug text-charcoal">
        {name}
      </h3>

      {rating ? (
        <div className="mt-5" data-testid={`product-rating-${code}`}>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-charcoal/45 mb-2">
            Customer reviews
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="flex text-amber-600">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  size={14}
                  fill={i < Math.round(rating) ? "currentColor" : "none"}
                  strokeWidth={1.5}
                />
              ))}
            </span>
            <span className="font-mono text-xs text-charcoal/60">
              {rating.toFixed(1)} out of 5 stars · {ratingsCount.toLocaleString()} ratings
            </span>
          </div>
        </div>
      ) : (
        <p className="mt-5 font-mono text-xs uppercase tracking-widest text-charcoal/50">
          Freshly shortlisted — see reviews on Amazon
        </p>
      )}

      {takeaway && (
        <p className="mt-4 text-sm leading-relaxed text-charcoal/70 italic font-serif">
          "{takeaway}"
        </p>
      )}

      <div className="mt-auto pt-7 flex justify-end">
        <a
          data-testid={`product-view-amazon-btn-${code}`}
          href={amazonUrl}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] border border-charcoal/25 px-4 py-2.5 text-charcoal hover:bg-charcoal hover:text-parchment hover:border-charcoal transition-colors duration-300"
        >
          View on Amazon <ArrowUpRight size={13} />
        </a>
      </div>
      <p className="mt-3 font-mono text-[10px] tracking-wide text-charcoal/40">
        Opens Amazon · same price to you, we may earn a commission
      </p>
      </motion.article>

      {image &&
        createPortal(
          <AnimatePresence>
            {zoomOpen && (
              <motion.div
                data-testid={`product-zoom-modal-${code}`}
                className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={() => setZoomOpen(false)}
              >
                <div className="absolute inset-0 bg-charcoal/90 backdrop-blur-sm" aria-hidden="true" />
                <motion.div
                  role="dialog"
                  aria-modal="true"
                  aria-label={`${name} photo`}
                  className="relative z-10 w-full max-w-3xl bg-parchment border hairline shadow-2xl"
                  initial={{ opacity: 0, scale: 0.96, y: 12 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96, y: 12 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    data-testid={`product-zoom-close-${code}`}
                    onClick={() => setZoomOpen(false)}
                    aria-label="Close photo"
                    className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4 z-20 flex items-center justify-center w-9 h-9 rounded-full bg-charcoal text-parchment hover:bg-brass transition-colors duration-200"
                  >
                    <X size={16} />
                  </button>
                  <div className="bg-white p-6 sm:p-10">
                    <img
                      data-testid={`product-zoom-image-${code}`}
                      src={image}
                      alt={name}
                      className="w-full max-h-[65vh] object-contain"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 border-t hairline">
                    <div>
                      <span className="font-mono text-xs uppercase tracking-[0.2em] font-medium text-amber-800">
                        {code}
                      </span>
                      <h3 className="mt-1 font-serif text-lg sm:text-xl text-charcoal">{name}</h3>
                    </div>
                    <a
                      data-testid={`product-zoom-view-amazon-btn-${code}`}
                      href={amazonUrl}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      className="inline-flex items-center justify-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] border border-charcoal/25 px-4 py-2.5 text-charcoal hover:bg-charcoal hover:text-parchment hover:border-charcoal transition-colors duration-300 whitespace-nowrap"
                    >
                      View on Amazon <ArrowUpRight size={13} />
                    </a>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </>
  );
};
